/**
 * Text size utilities for consistent typography across the application
 * Consolidates text size patterns from Node and User components
 */

/**
 * Standard text size variants
 * Used by most components for general text sizing
 */
export type TextSize = 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl'

/**
 * Avatar-specific text size variants
 * Used for avatar fallback text which requires very small sizes
 */
export type AvatarTextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

/**
 * Standard text size mapping
 * Maps size variants to Tailwind CSS classes
 */
export const textSizeClasses: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  md: 'text-sm', // 'md' maps to text-sm for backwards compatibility with User components
  lg: 'text-lg',
  xl: 'text-xl',
}

/**
 * Avatar-specific text size mapping
 * Uses smaller sizes for avatar fallback text
 */
export const avatarTextSizeClasses: Record<AvatarTextSize, string> = {
  xs: 'text-[6px]',
  sm: 'text-[8px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-lg',
  xxl: 'text-lg',
}

/**
 * Get text size classes for standard components
 * Provides a function-based API with default value support
 *
 * @param size - The text size variant
 * @param defaultSize - The default size to use if size is undefined
 * @returns Tailwind CSS class string
 *
 * @example
 * ```tsx
 * <p className={getTextSizeClasses('sm')}>Small text</p>
 * <p className={getTextSizeClasses(size, 'base')}>Text with default</p>
 * ```
 */
export function getTextSizeClasses(size?: TextSize, defaultSize: TextSize = 'sm'): string {
  return textSizeClasses[size || defaultSize]
}

/**
 * Get text size classes for avatar components
 * Provides a function-based API with default value support for avatar-specific sizing
 *
 * @param size - The avatar text size variant
 * @param defaultSize - The default size to use if size is undefined
 * @returns Tailwind CSS class string
 *
 * @example
 * ```tsx
 * <AvatarFallback className={getAvatarTextSizeClasses('md')}>KP</AvatarFallback>
 * ```
 */
export function getAvatarTextSizeClasses(
  size?: AvatarTextSize,
  defaultSize: AvatarTextSize = 'md'
): string {
  return avatarTextSizeClasses[size || defaultSize]
}
